import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
    Clock,
    Calendar,
    Palmtree,
    Bell,
    BarChart3,
    CheckCircle2,
    ArrowRight,
    Users,
    Building2,
    Shield,
    Zap,
    Globe,
    ChevronRight,
} from "lucide-react";

export const LandingPage = () => {
    const features = [
        {
            icon: Clock,
            title: "Smart Time Tracking",
            description:
                "Clock in and out with GPS verification. Automatic geo-fencing flags entries outside designated areas for review.",
            color: "bg-orange-500",
        },
        {
            icon: Calendar,
            title: "Intelligent Scheduling",
            description:
                "Create and manage employee schedules with drag-and-drop ease. Advanced forecasting helps you plan ahead.",
            color: "bg-teal-500",
        },
        {
            icon: Palmtree,
            title: "Leave Management",
            description:
                "Streamlined vacation requests with instant approval workflows. Track PTO balances and prevent scheduling conflicts.",
            color: "bg-amber-500",
        },
        {
            icon: Bell,
            title: "Real-Time Notifications",
            description:
                "Keep everyone in the loop with instant alerts for schedule changes, approvals, and important updates.",
            color: "bg-emerald-500",
        },
        {
            icon: BarChart3,
            title: "Powerful Reports",
            description:
                "Export detailed analytics in PDF or CSV format. Track hours, attendance patterns, and workforce insights.",
            color: "bg-orange-600",
        },
    ];

    const steps = [
        {
            number: "01",
            title: "Sign Up Free",
            description:
                "Create your organization account in under 2 minutes. No credit card required.",
        },
        {
            number: "02",
            title: "Add Your Team",
            description:
                "Invite employees via email or share your organization code. Set roles and permissions.",
        },
        {
            number: "03",
            title: "Start Managing",
            description:
                "Create schedules, track time, and manage leave requests from one central dashboard.",
        },
    ];

    const stats = [
        { value: "10K+", label: "Active Users" },
        { value: "500+", label: "Organizations" },
        { value: "99.9%", label: "Uptime" },
        { value: "4.9/5", label: "User Rating" },
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <img src="/logo.png" alt="TeamTime" className="w-10 h-10 rounded-xl" />
                            <span className="text-xl font-bold text-slate-900">TeamTime</span>
                        </Link>

                        <div className="hidden md:flex items-center gap-8">
                            <Link
                                to="/features"
                                className="text-slate-600 hover:text-slate-900 transition-colors"
                            >
                                Features
                            </Link>
                            <Link
                                to="/pricing"
                                className="text-slate-600 hover:text-slate-900 transition-colors"
                            >
                                Pricing
                            </Link>
                            <Link
                                to="/faq"
                                className="text-slate-600 hover:text-slate-900 transition-colors"
                            >
                                FAQ
                            </Link>
                        </div>

                        <div className="flex items-center gap-3">
                            <Link to="/login">
                                <Button variant="ghost" className="hidden sm:inline-flex">
                                    Log In
                                </Button>
                            </Link>
                            <Link to="/register">
                                <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg shadow-orange-500/25">
                                    Start Free Trial
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-orange-50/30 to-teal-50/30">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8">
                            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium">
                                <Zap className="w-4 h-4" />
                                Trusted by 500+ organizations worldwide
                            </div>

                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                                Workforce Management
                                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-teal-500">
                                    Made Simple
                                </span>
                            </h1>

                            <p className="text-lg sm:text-xl text-slate-600 max-w-xl">
                                Track time, create schedules, and manage leave requests—all in one
                                powerful platform. Built for teams of any size, across any industry.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/register">
                                    <Button
                                        size="lg"
                                        className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-lg px-8 py-6 shadow-xl shadow-orange-500/25"
                                    >
                                        Start Free Trial
                                        <ArrowRight className="ml-2 w-5 h-5" />
                                    </Button>
                                </Link>
                                <Link to="/pricing">
                                    <Button
                                        size="lg"
                                        variant="outline"
                                        className="w-full sm:w-auto text-lg px-8 py-6 border-2"
                                    >
                                        View Pricing
                                    </Button>
                                </Link>
                            </div>

                            <div className="flex items-center gap-6 pt-4">
                                <div className="flex -space-x-3">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <div
                                            key={i}
                                            className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold"
                                        >
                                            {String.fromCharCode(64 + i)}
                                        </div>
                                    ))}
                                </div>
                                <div className="text-sm text-slate-600">
                                    <span className="font-semibold text-slate-900">500+</span> teams
                                    already using TeamTime
                                </div>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-teal-500/20 rounded-3xl blur-3xl"></div>
                            <div className="relative bg-white rounded-3xl shadow-2xl p-2 border border-slate-200">
                                <img
                                    src="/images/hero.png"
                                    alt="Team collaboration"
                                    className="rounded-2xl w-full h-auto"
                                />
                            </div>

                            {/* Floating Cards */}
                            <div className="absolute -left-8 top-1/4 bg-white rounded-2xl shadow-xl p-4 border border-slate-100 animate-pulse">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                                        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">
                                            Clock In
                                        </p>
                                        <p className="text-xs text-slate-500">9:00 AM • On-site</p>
                                    </div>
                                </div>
                            </div>

                            <div className="absolute -right-4 bottom-1/4 bg-white rounded-2xl shadow-xl p-4 border border-slate-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                                        <Calendar className="w-6 h-6 text-orange-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">
                                            New Schedule
                                        </p>
                                        <p className="text-xs text-slate-500">Week of Jan 15</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Bar */}
            <section className="py-12 bg-gradient-to-r from-slate-900 to-slate-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-3xl sm:text-4xl font-bold text-white">
                                    {stat.value}
                                </div>
                                <div className="text-slate-400 mt-1">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <Zap className="w-4 h-4" />
                            Powerful Features
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                            Everything You Need to Manage Your Workforce
                        </h2>
                        <p className="text-lg text-slate-600">
                            From time tracking to analytics, TeamTime gives you the tools to
                            streamline operations and keep your team productive.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className="group p-8 rounded-2xl bg-white border border-slate-200 hover:border-transparent hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                            >
                                <div
                                    className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
                                >
                                    <feature.icon className="w-7 h-7 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">
                                    {feature.title}
                                </h3>
                                <p className="text-slate-600 leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        ))}

                        {/* CTA Card - View All Features */}
                        <div className="p-8 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white flex flex-col justify-center">
                            <h3 className="text-xl font-bold mb-3">Discover All Features</h3>
                            <p className="text-orange-100 mb-6">
                                Explore our complete feature set and see how TeamTime can transform
                                your workforce management.
                            </p>
                            <Link to="/features">
                                <Button
                                    size="lg"
                                    className="w-full bg-white text-orange-600 hover:bg-orange-50"
                                >
                                    View All Features
                                    <ChevronRight className="ml-2 w-5 h-5" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section
                id="how-it-works"
                className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-teal-50/30"
            >
                <div className="max-w-7xl mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                            <CheckCircle2 className="w-4 h-4" />
                            Simple Setup
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                            Get Started in Minutes
                        </h2>
                        <p className="text-lg text-slate-600">
                            No complex setup required. Start managing your workforce in three simple
                            steps.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {steps.map((step, index) => (
                            <div key={index} className="relative">
                                {index < steps.length - 1 && (
                                    <div className="hidden md:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-orange-300 to-transparent -translate-x-1/2"></div>
                                )}
                                <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
                                    <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-teal-500 mb-4">
                                        {step.number}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-3">
                                        {step.title}
                                    </h3>
                                    <p className="text-slate-600">{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Trust Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                                Built for Teams of All Sizes
                            </h2>
                            <p className="text-lg text-slate-600 mb-8">
                                Whether you're a small business or a growing enterprise, TeamTime
                                scales with your needs. Our flexible pricing ensures you only pay
                                for what you use.
                            </p>

                            <div className="space-y-4">
                                {[
                                    { icon: Users, text: "Free tier for teams up to 5 employees" },
                                    {
                                        icon: Building2,
                                        text: "Pro tier supports up to 50 team members",
                                    },
                                    {
                                        icon: Globe,
                                        text: "Enterprise tier with unlimited employees",
                                    },
                                    {
                                        icon: Shield,
                                        text: "Bank-grade security and data protection",
                                    },
                                ].map((item, index) => (
                                    <div key={index} className="flex items-center gap-4">
                                        <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                                            <item.icon className="w-5 h-5 text-teal-600" />
                                        </div>
                                        <span className="text-slate-700">{item.text}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-8">
                                <Link to="/pricing">
                                    <Button
                                        size="lg"
                                        className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white shadow-lg shadow-teal-500/25"
                                    >
                                        Compare Plans
                                        <ArrowRight className="ml-2 w-5 h-5" />
                                    </Button>
                                </Link>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-orange-500/20 rounded-3xl blur-3xl"></div>
                            <div className="relative bg-white rounded-3xl shadow-2xl p-2 border border-slate-200">
                                <img
                                    src="/images/boardroom_meeting.png"
                                    alt="Team working together"
                                    className="rounded-2xl w-full h-auto"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                        Ready to Simplify Your Workforce Management?
                    </h2>
                    <p className="text-xl text-orange-100 mb-8">
                        Join thousands of organizations that trust TeamTime to manage their teams
                        efficiently.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/register">
                            <Button
                                size="lg"
                                className="w-full sm:w-auto bg-white text-orange-600 hover:bg-orange-50 text-lg px-8 py-6 shadow-xl"
                            >
                                Start Your Free Trial
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </Link>
                        <Link to="/pricing">
                            <Button
                                size="lg"
                                variant="outline"
                                className="w-full sm:w-auto text-lg px-8 py-6 border-2 border-white text-white hover:bg-white/10"
                            >
                                View Pricing
                            </Button>
                        </Link>
                    </div>
                    <p className="mt-6 text-orange-100 text-sm">
                        No credit card required • Free plan available • Cancel anytime
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-slate-900 text-slate-400 py-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-12 mb-12">
                        <div className="md:col-span-1">
                            <Link to="/" className="flex items-center gap-2 mb-4">
                                <img
                                    src="/logo.png"
                                    alt="TeamTime"
                                    className="w-10 h-10 rounded-xl"
                                />
                                <span className="text-xl font-bold text-white">TeamTime</span>
                            </Link>
                            <p className="text-sm">
                                Workforce management made simple. Track time, create schedules, and
                                manage your team efficiently.
                            </p>
                        </div>

                        <div>
                            <h4 className="text-white font-semibold mb-4">Product</h4>
                            <ul className="space-y-2 text-sm">
                                <li>
                                    <Link
                                        to="/features"
                                        className="hover:text-white transition-colors"
                                    >
                                        Features
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/pricing"
                                        className="hover:text-white transition-colors"
                                    >
                                        Pricing
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/faq" className="hover:text-white transition-colors">
                                        FAQ
                                    </Link>
                                </li>
                                <li>
                                    <a href="#" className="hover:text-white transition-colors">
                                        Integrations
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-white font-semibold mb-4">Company</h4>
                            <ul className="space-y-2 text-sm">
                                <li>
                                    <a href="#" className="hover:text-white transition-colors">
                                        About Us
                                    </a>
                                </li>
                                <li>
                                    <a href="#" className="hover:text-white transition-colors">
                                        Careers
                                    </a>
                                </li>
                                <li>
                                    <a href="#" className="hover:text-white transition-colors">
                                        Blog
                                    </a>
                                </li>
                                <li>
                                    <a href="#" className="hover:text-white transition-colors">
                                        Contact
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-white font-semibold mb-4">Legal</h4>
                            <ul className="space-y-2 text-sm">
                                <li>
                                    <a href="#" className="hover:text-white transition-colors">
                                        Privacy Policy
                                    </a>
                                </li>
                                <li>
                                    <a href="#" className="hover:text-white transition-colors">
                                        Terms of Service
                                    </a>
                                </li>
                                <li>
                                    <a href="#" className="hover:text-white transition-colors">
                                        Security
                                    </a>
                                </li>
                                <li>
                                    <a href="#" className="hover:text-white transition-colors">
                                        GDPR
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <p className="text-sm">
                            © {new Date().getFullYear()} TeamTime. All rights reserved.
                        </p>
                        <div className="flex gap-6">
                            <a href="#" className="hover:text-white transition-colors">
                                Twitter
                            </a>
                            <a href="#" className="hover:text-white transition-colors">
                                LinkedIn
                            </a>
                            <a href="#" className="hover:text-white transition-colors">
                                GitHub
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};
